import './styles.scss'

import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Divider, Row } from 'antd'

import B2becButton from '@/components/B2becButton'
import { useAppSelector } from '@/store/hook'
import { selectAddressScheme, selectCountryNames } from '@/store/selectors/configSelector'
import { ADDRESS_SCHEME_TYPES } from '@/libs/constants/configs'
import { linkGenerator } from '@/libs/utils/language'

const RequestServiceConfirmation = ({ data }) => {
  const {
    customerCompanyName = '',
    materialName = '',
    materialNumber = '',
    customerAddress = '',
    customerCity = '',
    customerZipCode = '',
    customerRegion = '',
    countryCode = '',
    caseType = '',
  } = data

  const { t } = useTranslation()
  const navigate = useNavigate()

  const countryNamesConfig = useAppSelector(selectCountryNames)
  const addressScheme = useAppSelector(selectAddressScheme)
  const isStandardScheme = addressScheme === ADDRESS_SCHEME_TYPES.STANDARD

  const countryName = useMemo(() => {
    const foundCountryName = countryNamesConfig.find(
      (country) => country.value === countryCode,
    )

    return foundCountryName ? foundCountryName.name : countryCode
  }, [countryCode, countryNamesConfig])

  const location = useMemo(() => {
    const locationParts = isStandardScheme
      ? [customerZipCode, customerCity]
      : [customerZipCode, customerRegion, customerCity]

    return locationParts.filter(Boolean).join(' ')
  }, [customerZipCode, customerRegion, customerCity, isStandardScheme])

  const st = useCallback((key) => t(`service.confirmationPage.${key}`), [t])

  return (
    <div className="service-confirmation">
      <h1 className="service-confirmation__header">{st('header')}</h1>
      <div className="service-confirmation__main">
        <div className="service-block">
          <div className="service-block__header">
            <h2 className="font-weight-bold">
              {st('serviceInformation.header')}
            </h2>
            <span>{st('serviceInformation.message')}</span>
          </div>
          <div className="service-block__content">
            <Row gutter={[20, 20]}>
              <Col md={12} span={24}>
                <div className="service-information">
                  <span className="font-weight-bold">
                    {st('serviceSummary.header')}
                  </span>
                  <span>{materialName}</span>
                  <span>
                    {`${st('serviceSummary.itemNumber')}: ${materialNumber}`}
                  </span>
                  <span>
                    {`${st('serviceSummary.serviceType')}: ${
                      st(
                        `serviceTypes.${caseType}`,
                      )
                    }`}
                  </span>
                </div>
              </Col>
              <Col md={12} span={24}>
                <div className="service-information">
                  <span className="font-weight-bold">
                    {st('addressDate.header')}
                  </span>
                  <span>{st('addressDate.address')}:</span>
                  <span>{customerCompanyName}</span>
                  <span>{customerAddress}</span>
                  <span>{location}</span>
                  <span>{countryName}</span>
                </div>
              </Col>
            </Row>
          </div>
        </div>
        <Divider />
        <div className="service-block service-confirmation__whatsnext">
          <div className="service-block__header service-block__header--center">
            <h2 className="font-weight-bold">{st('whatsnext')}</h2>
          </div>
          <div className="service-block__content">
            <Row gutter={[20, 20]}>
              <Col md={{ span: 12, order: 1 }} order={2} span={24}>
                <B2becButton
                  block
                  ghost
                  onClick={() => navigate(linkGenerator('/my-workspace'))}
                >
                  {st('button.backToWorkspace')}
                </B2becButton>
              </Col>
              <Col md={{ span: 12, order: 2 }} order={1} span={24}>
                <B2becButton
                  block
                  onClick={() => navigate(linkGenerator('/my-machines'))}
                >
                  {st('button.myMachines')}
                </B2becButton>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RequestServiceConfirmation
